import { useEffect, useState } from 'react';
import { useAppSelector } from '@/Redux/store';
import { resolveTheme } from '@/Redux/Slices/uiSlice';

/**
 * The theme actually in force, `system` already resolved.
 *
 * This is React state rather than a bare call to `resolveTheme` because under
 * `system` nothing in Redux changes when the OS flips — so a component that
 * merely read the media query at render time would keep whatever it read on
 * first paint. The mark in particular has to repaint: its tile is cream on a
 * light ground and brown on a dark one, and a stale one leaves a pale square
 * sitting on black.
 */
export function useResolvedTheme(): 'light' | 'dark' {
  const preference = useAppSelector((s) => s.ui.theme);
  const [resolved, setResolved] = useState(() => resolveTheme(preference));

  useEffect(() => {
    const apply = () => setResolved(resolveTheme(preference));
    apply();

    if (preference !== 'system' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, [preference]);

  return resolved;
}
